import moment, { Moment } from 'moment';
import * as yup from 'yup';

import {
  QuestionnaireItemFormat, AccountingVendor,
  AccountingVendorField,
} from '../../types';
import { ExpectedAnswer } from '../../constants/constants';

const EMAIL_REGEX =
  /^(([a-zA-Z0-9._+-]+(\.[a-zA-Z0-9._+-]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}))$/;
const USER_ID_REGEX = /^[a-z0-9][a-z0-9._-]{1,78}[a-z0-9]$/;
const USER_ID_MESSAGE =
  '3-80 chars: a-z, 0-9, . _ - only; start/end with a letter or digit';
// Trims and lowercases before validating; blank stays valid (optional field).
const userIdSchema = yup
  .string()
  .transform((value) => (typeof value === 'string' ? value.trim().toLowerCase() : value))
  .matches(USER_ID_REGEX, { message: USER_ID_MESSAGE, excludeEmptyString: true });
const validateDateField = (minDateValue?: string, maxDateValue?: string, isOptional = false) => {
  return yup
    .mixed()
    .test('isValidDate', 'Invalid date format', function (value) {
      if (isOptional && (value === null || value === '')) {
        return true;
      }
      if (value === null || value === '') {
        return this.createError({
          message: 'Date field is required',
          path: this.path,
        });
      }

      return yup.date().isValidSync(value);
    })
    .test('isWithinRange', 'Invalid date', function (value) {
      const minDate = new Date(minDateValue || '1900-01-01');
      const maxDate = new Date(maxDateValue || '2100-01-01');

      if (value === null || value === '') {
        // Skip validation if the field is null or empty, as it's already handled in the previous test
        return true;
      }

      const date = new Date(value as string);

      return date >= minDate && date <= maxDate;
    });
};

export const manageAccountFormSchema = yup.object({
  id: yup.number().required('Required *'),
  role: yup.string().required('Required *'),
  firstName: yup
    .string()
    .max(100, 'Must be less or equal to 100 characters')
    .required('Required *'),
  lastName: yup.string().max(100, 'Must be less or equal to 100 characters').required('Required *'),
  email: yup.string().matches(EMAIL_REGEX, 'Invalid email format').required('Required *'),
  phone: yup.string().required('Required *'),
  isPinSet: yup.boolean().required(),
});

export const createUserFormSchema = yup.object({
  id: yup.number(),
  firstName: yup
    .string()
    .max(100, 'Must be less or equal to 100 characters')
    .required('Required *'),
  lastName: yup.string().max(100, 'Must be less or equal to 100 characters').required('Required *'),
  email: yup.string().matches(EMAIL_REGEX, 'Invalid email format').required('Required *'),
  phone: yup
    .string()
    .required('Required *')
    .matches(/^\+\d{1,15}$/, 'The phone number must start with + and contain up to 15 digits'),
  role: yup.string().optional(),
  roleId: yup.string().required('Required *'),
  firmId: yup.string().required('Required *'),
  status: yup.string(),
});

export const defendantFormSchema = yup.object({
  id: yup.string(),
  referenceId: yup
    .string()
    .max(100, 'Must be less or equal to 100 characters')
    .required('Required *'),
  name: yup.string().max(300, 'Must be less or equal to 300 characters').required('Required *'),
  firmId: yup.string().required('Required *'),
});

export const clientCreateFormSchema = yup.object({
  autoGenerateClientEmail: yup.boolean(),
  autoGenerateClientAddress: yup.boolean(),
  onboardingOption: yup
    .string()
    .oneOf(
      ['emailLinkOnly', 'phoneOtpOnly', 'emailOtpOnly', 'emailLinkPhoneOtp', 'emailOtpPhoneOtp'],
      'Invalid onboarding option',
    )
    .required('Required *'),
  referenceId: yup
    .string()
    .max(100, 'Must be less or equal to 100 characters')
    .required('Required *'),
  userId: userIdSchema,
  firstName: yup
    .string()
    .max(40, 'Must be less or equal to 40 characters')
    .required('Required *')
    .matches(/^[a-zA-Z0-9_,.&()/"'\s-]+$/, 'Valid characters: [a-z A-Z 0-9 _ , . & () / " \' -]'),
  middleName: yup
    .string()
    .max(40, 'Must be less or equal to 40 characters')
    .matches(/^[a-zA-Z0-9_,.&()/"'\s-]*$/, 'Valid characters: [a-z A-Z 0-9 _ , . & () / " \' -]'),
  lastName: yup
    .string()
    .max(40, 'Must be less or equal to 40 characters')
    .required('Required *')
    .matches(/^[a-zA-Z0-9_,.&()/"'\s-]+$/, 'Valid characters: [a-z A-Z 0-9 _ , . & () / " \' -]'),
  email: yup.string().when('autoGenerateClientEmail', {
    is: true,
    then: (schema) => schema.matches(EMAIL_REGEX, { message: 'Invalid email format', excludeEmptyString: true }),
    otherwise: (schema) => schema.matches(EMAIL_REGEX, 'Invalid email format').required('Required *'),
  }),
  phone: yup.string().matches(/^\+1\d{10}$/, {
    message: 'Enter a 10-digit US mobile number after +1',
    excludeEmptyString: true,
  }),
  address1: yup.string().when('autoGenerateClientAddress', {
    is: true,
    then: (schema) =>
      schema
        .transform((value) => value || undefined)
        .min(4, 'Must be between 4 and 40 characters ')
        .max(40, 'Must be less or equal to 40 characters')
        .optional(),
    otherwise: (schema) =>
      schema
        .min(4, 'Must be between 4 and 40 characters ')
        .max(40, 'Must be less or equal to 40 characters')
        .required('Required *'),
  }),
  address2: yup.string().max(30, 'Must be less or equal to 30 characters'),
  city: yup.string().when('autoGenerateClientAddress', {
    is: true,
    then: (schema) =>
      schema
        .max(45, 'Must be less or equal to 45 characters')
        .optional()
        // eslint-disable-next-line quotes
        .matches(/^[a-zA-Z.'-\s]+$/, { message: `Valid characters: a-z, A-Z, ., ', -`, excludeEmptyString: true }),
    otherwise: (schema) =>
      schema
        .max(45, 'Must be less or equal to 45 characters')
        .required('Required *')
        // eslint-disable-next-line quotes
        .matches(/^[a-zA-Z.'-\s]+$/, `Valid characters: a-z, A-Z, ., ', -`),
  }),
  state: yup.string().when('autoGenerateClientAddress', {
    is: true,
    then: (schema) => schema.optional(),
    otherwise: (schema) => schema.required('Required *'),
  }),
  zip: yup.string().when('autoGenerateClientAddress', {
    is: true,
    then: (schema) =>
      schema
        .optional()
        .matches(
          /^(?!0{5})(\d{5}|\d{5}-\d{4})$/,
          { message: 'Invalid Zip Code. Correct format: XXXXX or XXXXX-XXXX', excludeEmptyString: true },
        ),
    otherwise: (schema) =>
      schema
        .required('Required *')
        .matches(
          /^(?!0{5})(\d{5}|\d{5}-\d{4})$/,
          'Invalid Zip Code. Correct format: XXXXX or XXXXX-XXXX',
        ),
  }),
  birthdate: yup
    .date()
    .transform((_, rawValue) => {
      if (!rawValue) {
        return null;
      }

      const correctDate = moment(rawValue, ['mm-dd-yyyy']).toDate();
      return correctDate;
    })
    .nullable()
    .notRequired()
    .min(moment(new Date(1900, 1, 1)).toDate(), 'Date is too early')
    .typeError('Invalid date')
    .max(moment(), 'Date is too far'),
  ssn: yup.string().matches(/^(?!0{3})\d{3}-(?!0{2})\d{2}-(?!0{4})\d{4}$/, {
    message: 'Invalid SSN. Correct format: XXX-XX-XXXX',
    excludeEmptyString: true,
  }),
  onboardingLinkDeliveryChannel: yup.string().nullable().notRequired(),
  onboardingOtpChannel: yup.string().nullable().notRequired(),
});

export const clientEditFormSchema = yup.object({
  id: yup.string().required(),
  onboardingOption: yup
    .string()
    .oneOf(
      ['emailLinkOnly', 'phoneOtpOnly', 'emailOtpOnly', 'emailLinkPhoneOtp', 'emailOtpPhoneOtp'],
      'Invalid onboarding option',
    )
    .required('Required *'),
  referenceId: yup
    .string()
    .max(100, 'Must be less or equal to 100 characters')
    .required('Required *'),
  userId: userIdSchema,
  firstName: yup
    .string()
    .max(40, 'Must be less or equal to 40 characters')
    .required('Required *')
    .matches(/^[a-zA-Z0-9_,.&()/"'\s-]+$/, 'Valid characters: [a-z A-Z 0-9 _ , . & () / " \' -]'),
  middleName: yup
    .string()
    .max(40, 'Must be less or equal to 40 characters')
    .matches(/^[a-zA-Z0-9_,.&()/"'\s-]*$/, 'Valid characters: [a-z A-Z 0-9 _ , . & () / " \' -]'),
  lastName: yup
    .string()
    .max(40, 'Must be less or equal to 40 characters')
    .required('Required *')
    .matches(/^[a-zA-Z0-9_,.&()/"'\s-]+$/, 'Valid characters: [a-z A-Z 0-9 _ , . & () / " \' -]'),
  email: yup.string().matches(EMAIL_REGEX, 'Invalid email format').required('Required *'),
  phone: yup.string().matches(/^\+1\d{10}$/, {
    message: 'Invalid phone number',
    excludeEmptyString: true,
  }),
  address1: yup
    .string()
    .min(4, 'Must be between 4 and 40 characters ')
    .max(40, 'Must be less or equal to 40 characters')
    .required('Required *'),
  address2: yup.string().max(30, 'Must be less or equal to 30 characters'),
  city: yup
    .string()
    .max(45, 'Must be less or equal to 45 characters')
    .required('Required *')
    // eslint-disable-next-line quotes
    .matches(/^[a-zA-Z.'-\s]+$/, `Valid characters: a-z, A-Z, ., ', -`),
  state: yup.string().required('Required *'),
  zip: yup
    .string()
    .required('Required *')
    .matches(
      /^(?!0{5})(\d{5}|\d{5}-\d{4})$/,
      'Invalid Zip Code. Correct format: XXXXX or XXXXX-XXXX',
    ),
  onboardingLinkDeliveryChannel: yup.string().nullable().notRequired(),
  onboardingOtpChannel: yup.string().nullable().notRequired(),
});

export const personalInfoFormSchema = yup.object().shape({
  birthdate: yup.mixed().nullable(),
  ssn: yup.string(),
});

export const casesFormSchema = yup.object({
  id: yup.string(),
  referenceId: yup
    .string()
    .max(100, 'Must be less or equal to 100 characters')
    .required('Required *'),
  caseTypeCategory: yup.string().required('Required *'),
  type: yup.object().shape({
    id: yup.string().required('Required *'),
    label: yup.string().required('Required *'),
  }),
  trustAccount: yup
    .object()
    .shape({
      id: yup.string().required('Required *'),
      label: yup.string().required('Required *'),
    })
    .required('Required *'),
  firmId: yup.string().required('Required *'),
  filedAt: validateDateField(undefined, undefined, true).nullable(),
  sol: validateDateField(undefined, undefined, true).nullable(),
  clientId: yup.object().shape({
    id: yup.string().required('Required *'),
    label: yup.string().required('Required *'),
  }),
  defendant: yup.array().of(
    yup.object().shape({
      defendantId: yup.object().shape({
        id: yup.string().required('Required *'),
        label: yup.string().required('Required *'),
      }),
      gross: yup
        .string()
        .trim()
        .test('is-valid-amount', 'Invalid number', (value) => {
          if (!value || value === '') return true;
          return /^\d+(\.\d{1,2})?$/.test(value);
        })
        .test('is-in-range', 'Invalid range', (value) => {
          if (!value || value === '') return true;
          const num = Number(value);
          return Number.isFinite(num) && num >= 0.01 && num <= 99999999.99;
        }), // TODO: currency input
    }),
  ),
  secondaryFirms: yup.array().of(
    yup.object().shape({
      firmId: yup.object().shape({
        id: yup.string(),
        label: yup.string(),
      }),
      firmRole: yup.string().required(),
    }),
  ),
});

export const casesEditFormSchema = yup.object({
  id: yup.string(),
  name: yup.string(),
  referenceId: yup
    .string()
    .max(100, 'Must be less or equal to 100 characters')
    .required('Required *'),
  caseTypeCategory: yup.string().required('Required *'),
  type: yup
    .object()
    .shape({ id: yup.string().required('Required *'), label: yup.string().required('Required *') }),
  trustAccount: yup
    .object()
    .shape({
      id: yup.string().required('Required *'),
      label: yup.string().required('Required *')
    })
    .required('Required *'),
  filedAt: validateDateField(undefined, undefined, true).nullable(),
  sol: validateDateField(undefined, undefined, true).nullable(),
  clientId: yup
    .object()
    .shape({ id: yup.string().required('Required *'), label: yup.string().required('Required *') }),
  defendant: yup.array().of(
    yup.object().shape({
      defendantId: yup.object().shape({
        id: yup.string().required('Required *'),
        label: yup.string().required('Required *'),
      }),
      gross: yup
        .string()
        .trim()
        .test('is-valid-amount', 'Invalid number', (value) => {
          if (!value || value === '') return true;
          return /^\d+(\.\d{1,2})?$/.test(value);
        })
        .test('is-in-range', 'Invalid range', (value) => {
          if (!value || value === '') return true;
          const num = Number(value);
          return Number.isFinite(num) && num >= 0.01 && num <= 99999999.99;
        }),
    }),
  ),
  secondaryFirms: yup.array().of(
    yup.object().shape({
      firmId: yup.object().shape({
        id: yup.string(),
        label: yup.string(),
      }),
      firmRole: yup.string().required(),
    }),
  ),
  paymentStarted: yup.boolean().required(),
});

// const remittanceLineShape = yup.object({
//   amount: yup.number().nullable().required('Required *').min(0.01, 'Invalid range').max(99999999.99, 'Invalid range'),
//   values: yup.object().required('Required *'),
// });
const remittanceLineShape = yup.object({
  id: yup.mixed().optional(),

  sequence: yup.number().optional(),

  amount: yup
    .number()
    .nullable()
    .optional()
    .transform((value, originalValue) => {
      if (
        originalValue === '' ||
        originalValue === null ||
        originalValue === undefined
      ) {
        return null;
      }
      return value;
    }),

  values: yup
    .object()
    .required(),
});


export const deductionsCreateFormSchema = yup.object({
  id: yup.string(),
  isAssigned: yup.bool(),
  amount: yup
    .string()
    .required('Required *')
    .test('is-in-range', 'Invalid range', (value) => {
      if (!value || value === '') return false;
      const num = Number(value);
      return Number.isFinite(num) && num >= 0 && num <= 99999999.99;
    })
    .test('valid-numeric-input', 'Only numeric values are allowed', function (value) {
      if (!value) return true;
      return /^\d+(\.\d{1,2})?$/.test(value);
    }),
  type: yup.string().required('Required *'),
  defendant: yup.object({
    id: yup.string(),
    label: yup.string(),
  }),
  disposition: yup.string().when('type', {
    is: (value: string) => value === 'Lien' || value === 'Service Provider',
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }),
  paymentMethod: yup.string().when('type', {
    is: (value: string) => value === 'Lien' || value === 'Service Provider',
    then: (schema) => schema.optional(), // visible when 'Lien' or 'Service Provider' but not required
    otherwise: (schema) => schema.optional(),
  }),
  remittanceContext: yup.object().nullable().optional(),
  remittanceLines: yup.array().when(['paymentMethod', 'remittanceContext'], {
    is: (
      paymentMethod: string | undefined,
      remittanceContext: any, // eslint-disable-line @typescript-eslint/no-explicit-any
    ) => (paymentMethod || '').toUpperCase() === 'CHECK' && !!remittanceContext?.remittanceTemplate,
    then: (schema) => schema.of(remittanceLineShape).required('Required *'),
    otherwise: (schema) => schema.of(remittanceLineShape).optional(),
  }),
  paymentAccount: yup
    .object()
    .shape({
      id: yup.string().required('Required *'),
      label: yup.string().required('Required *'),
    })
    .nullable()
    .when(['type', 'disposition', 'paymentMethod'], {
      is: (type: string, disposition: string, paymentMethod: string) =>
        (type === 'Lien' || type === 'Service Provider') &&
        disposition !== 'External' &&
        ['ACH', 'CHECK'].includes((paymentMethod || '').toUpperCase()),
      then: (schema) => schema.required('Required *'),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
  vendorAccount: yup
    .object()
    .shape({
      id: yup.string().required('Required *'),
      label: yup.string().required('Required *'),
      defaultPaymentMethod: yup.string().required('Required *'),
    })
    .nullable()
    .when('type', {
      is: (value: string) => value === 'Lien' || value === 'Service Provider',
      then: (schema) => schema.required('Required *'),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
  // description: yup.string().max(1000, 'Must be less or equal to 1000 characters').optional(),
  memo: yup
    .string()
    .max(140, 'Must be less or equal to 140 characters')
    .when('type', {
      is: (value: string) => value === 'Lien' || value === 'Service Provider',
      then: (schema) => schema.optional(),
      otherwise: (schema) => schema.optional(),
    }),
  uploadInvoice: yup
    .mixed<File | string>()
    .nullable()
    .optional()
    .test('file-or-string', 'Invalid file type', (value) => value === null || typeof value === 'string' || value instanceof File),
  externalPaymentReference: yup.string().nullable().max(255, 'Must be less or equal to 255 characters'),
  externalPaymentDate: yup
    .date()
    .typeError('Invalid date')
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .max(new Date(), 'Date cannot be in the future'),
});

export interface RemittanceTemplateColumnValidation {
  key: string;
  label: string;
  maxLength: number;
  isRequired: boolean;
  binding: string;
}

export const createRemittanceFormSchema = (
  columns: RemittanceTemplateColumnValidation[],
) => {
  const shape = columns.reduce<
    Record<string, yup.StringSchema<string>>
  >((schema, column) => {
    let fieldSchema = yup.string().defined().trim();

    if (column.isRequired) {
      fieldSchema = fieldSchema.required('Required *');
    }

    if (column.maxLength > 0) {
      fieldSchema = fieldSchema.max(
        column.maxLength,
        `Must be less or equal to ${column.maxLength} characters`,
      );
    }

    if (column.binding === 'LINE_AMOUNT') {
      fieldSchema = fieldSchema
        .test(
          'valid-numeric-input',
          'Only numeric values are allowed',
          (value) => {
            if (!value) {
              return !column.isRequired;
            }

            const normalizedValue = value.replace(/[$,\s]/g, '');

            return /^\d+(\.\d{1,2})?$/.test(normalizedValue);
          },
        )
        .test(
          'is-in-range',
          'Invalid range',
          (value) => {
            if (!value) {
              return !column.isRequired;
            }

            const normalizedValue = value.replace(/[$,\s]/g, '');
            const amount = Number(normalizedValue);

            return (
              Number.isFinite(amount) &&
              amount >= 0.00 &&
              amount <= 99999999.99
            );
          },
        );
    }

    schema[column.key] = fieldSchema;

    return schema;
  }, {});

  return yup.object().shape(shape);
};

export const settlementsCreateFormSchema = yup.object({
  id: yup.string(),
  settlementType: yup.string().oneOf(['standard', 'pip', 'standard_pip', 'lien_repayment']),
  amount: yup
    .number()
    .typeError('Invalid value')
    .when(['autoFillMemo', 'settlementType'], {
      is: (autoFillMemo: boolean, settlementType: string) =>
        !autoFillMemo && settlementType !== 'pip',
      then: (schema) =>
        schema.when('settlementType', {
          // Settled amount may be zero when combined with a PIP or lien reimbursement amount
          is: (settlementType: string) =>
            settlementType === 'standard_pip' || settlementType === 'lien_repayment',
          then: (pipSchema) =>
            pipSchema.min(0, 'Invalid amount').max(99999999.99, 'Invalid amount').required('Required *'),
          otherwise: (settlementSchema) =>
            settlementSchema
              .min(0.01, 'Invalid amount')
              .max(99999999.99, 'Invalid amount')
              .required('Required *'),
        }),
      otherwise: (schema) => schema.optional().nullable(),
    }),
  pipAmount: yup
    .number()
    .typeError('Invalid value')
    .when(['autoFillMemo', 'settlementType'], {
      is: (autoFillMemo: boolean, settlementType: string) =>
        !autoFillMemo && (settlementType === 'pip' || settlementType === 'standard_pip'),
      then: (schema) =>
        schema.when('settlementType', {
          // Either amount may be zero for standard_pip, but not both.
          is: 'standard_pip',
          then: (pipSchema) =>
            pipSchema
              .min(0, 'Invalid amount')
              .max(99999999.99, 'Invalid amount')
              .required('Required *')
              .test(
                'settled-plus-pip-positive',
                'Settled amount and PIP amount cannot both be zero',
                (value, context) => (Number(context.parent.amount) || 0) + (Number(value) || 0) > 0,
              ),
          otherwise: (pipSchema) =>
            pipSchema
              .min(0.01, 'Invalid amount')
              .max(99999999.99, 'Invalid amount')
              .required('Required *'),
        }),
      otherwise: (schema) => schema.optional().nullable(),
    }),
  lienReimbursementAmount: yup
    .number()
    .typeError('Invalid value')
    .when(['autoFillMemo', 'settlementType'], {
      is: (autoFillMemo: boolean, settlementType: string) =>
        !autoFillMemo && settlementType === 'lien_repayment',
      // Either amount may be zero, but not both.
      then: (schema) =>
        schema
          .min(0, 'Invalid amount')
          .max(99999999.99, 'Invalid amount')
          .required('Required *')
          .test(
            'settled-plus-lien-reimbursement-positive',
            'Settled amount and lien reimbursement amount cannot both be zero',
            (value, context) => (Number(context.parent.amount) || 0) + (Number(value) || 0) > 0,
          ),
      otherwise: (schema) => schema.optional().nullable(),
    }),
  defendant: yup.object().when('autoFillMemo', {
    is: false,
    then: (schema) =>
      schema.shape({
        id: yup.string().required('Required *'),
        label: yup.string().required('Required *'),
      }),
    otherwise: (schema) => schema.optional(),
  }),
  autoFillMemo: yup.boolean(),
  uploadedMemo: yup
    .mixed<File>()
    .nullable()
    .when('autoFillMemo', {
      is: true,
      then: (schema) => schema.required('File is required'),
      otherwise: (schema) => schema.optional(),
    }),
});

export const deductionsEditFormSchema = yup.object({
  id: yup.string(),
  caseReferenceId: yup.string(),
  caseSystemName: yup.string(),
  firm: yup.string(),
  isAssigned: yup.bool(),
  defendant: yup.object({
    id: yup.string(),
    label: yup.string(),
  }),
  amount: yup
    .string()
    .trim()
    .required('Required *')
    .test('valid-numeric-input', 'Only numeric values are allowed', function (value) {
      if (!value) return true;
      return /^\d+(\.\d{1,2})?$/.test(value);
    })
    .test('is-in-range', 'Invalid range', (value) => {
      if (!value || value === '') return false;
      const num = Number(value);
      return Number.isFinite(num) && num >= 0 && num <= 99999999.99;
    })
    .test(
      'is-less-than-billed',
      'Settled amount must be less than billed amount',
      function (value) {
        const { preSettlementAmount } = this.parent;
        if (!value || !preSettlementAmount) return true;
        const agreedAmount = parseFloat(value);
        const billedAmount = parseFloat(preSettlementAmount);
        if (isNaN(agreedAmount) || isNaN(billedAmount)) return true;
        return agreedAmount <= billedAmount;
      },
    ),
  type: yup.string().required('Required *'),
  // disposition: yup.string().when('type', {
  //   is: (value: string) => value === 'Lien' || value === 'SERVICE_PROVIDER',
  //   then: (schema) => schema.required(),
  //   otherwise: (schema) => schema.optional(),
  // }),
  disposition: yup.string().required('Required *'),
  paymentMethod: yup
    .string()
    .nullable()
    .when('type', {
      is: (value: string) => value === 'Lien' || value === 'SERVICE_PROVIDER',
      then: (schema) => schema.optional(), // visible when 'Lien' or 'SERVICE_PROVIDER' but not required
      otherwise: (schema) => schema.optional(),
    }),
  remittanceContext: yup.object().nullable().optional(),
  remittanceLines: yup.array().when(['paymentMethod', 'remittanceContext'], {
    is: (paymentMethod: string | undefined, remittanceContext: any) => // eslint-disable-line @typescript-eslint/no-explicit-any
      (paymentMethod || '').toUpperCase() === 'CHECK' && !!remittanceContext?.remittanceTemplate,
    then: (schema) => schema.of(remittanceLineShape).required('Required *'),
    otherwise: (schema) => schema.of(remittanceLineShape).optional(),
  }),
  paymentAccount: yup
    .object()
    .shape({
      id: yup.string().required('Required *'),
      label: yup.string().required('Required *'),
    })
    .nullable()
    .when(['type', 'disposition', 'paymentMethod'], {
      is: (type: string, disposition: string, paymentMethod: string) =>
        (type === 'Lien' || type === 'SERVICE_PROVIDER') &&
        disposition !== 'External' &&
        ['ACH', 'CHECK'].includes((paymentMethod || '').toUpperCase()),
      then: (schema) => schema.required('Required *'),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
  vendorAccount: yup
    .object()
    .shape({
      id: yup.string(),
      label: yup.string(),
      defaultPaymentMethod: yup.string(),
      accounting: yup
        .object()
        .shape({
          isSynced: yup.boolean(),
          externalVendorId: yup.string().nullable(),
        })
        .nullable()
        .optional(),
    })
    .nullable()
    .when('type', {
      is: (value: string) => value === 'Lien' || value === 'SERVICE_PROVIDER',
      then: (schema) => schema.required('Required *'),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),

  // description: yup
  //   .string()
  //   .nullable()
  //   .max(1000, 'Must be less or equal to 1000 characters')
  //   .optional(),

  memo: yup
    .string()
    .nullable()
    .max(140, 'Must be less or equal to 140 characters')
    .when('type', {
      is: (value: string) => value === 'Lien' || value === 'SERVICE_PROVIDER',
      then: (schema) => schema.optional(),
      otherwise: (schema) => schema.optional(),
    }),

  uploadInvoice: yup
    .mixed<File | string>()
    .nullable()
    .optional()
    .test(
      'file-or-string',
      'Invalid file type',
      (value) => value === null || typeof value === 'string' || value instanceof File,
    ),

  preSettlementAmount: yup
    .string()
    .trim()
    .required('Required *')
    .test('valid-numeric-input', 'Only numeric values are allowed', function (value) {
      if (!value) return true;
      return /^\d+(\.\d{1,2})?$/.test(value);
    })
    .test('is-in-range', 'Invalid range', (value) => {
      if (!value || value === '') return false;
      const num = Number(value);
      return Number.isFinite(num) && num >= 0 && num <= 99999999.99;
    })
    .test(
      'is-not-greater-than-settlement',
      'Billed amount must not be greater than settlement amount',
      function (value) {
        const { settlementAmount } = this.parent;
        if (!value || !settlementAmount) return true;
        const preSettlement = parseFloat(value);
        const settlement = parseFloat(settlementAmount.toString());
        if (isNaN(preSettlement) || isNaN(settlement)) return true;
        return preSettlement <= settlement;
      },
    ),

  settlementAmount: yup.mixed().optional(),
  entryDate: yup.mixed().optional(),
  externalPaymentReference: yup
    .string()
    .nullable()
    .max(255, 'Must be less or equal to 255 characters'),
  // externalPaymentMemo: yup.string().nullable().optional().max(1000, 'Must be less or equal to 1000 characters'),
  externalPaymentDate: yup
    .date()
    .typeError('Invalid date')
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .max(new Date(), 'Date cannot be in the future'),
});

export const settlementDeductionsEditFormSchema = yup.object({
  id: yup.string(),
  isAssigned: yup.bool(),
  defendant: yup.object({
    id: yup.string(),
    label: yup.string(),
  }),
  amount: yup.string().required('Required *'),
  type: yup.string().required('Required *'),
  disposition: yup.string().when('type', {
    is: 'Lien',
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }),
  paymentMethod: yup.string().when('type', {
    is: 'Lien',
    then: (schema) => schema.optional(), // visible when 'Lien' but not required
    otherwise: (schema) => schema.optional(),
  }),
  vendorName: yup
    .object()
    .shape({
      id: yup.string().optional(),
      label: yup.string().optional(),
      defaultPaymentMethod: yup.string().optional(),
    })
    .nullable()
    .optional(),

  description: yup
    .string()
    .nullable()
    .max(1000, 'Must be less or equal to 1000 characters')
    .optional(),

  memo: yup
    .string()
    .nullable()
    .max(500, 'Must be less or equal to 500 characters')
    .when('type', {
      is: 'Lien',
      then: (schema) => schema.optional(),
      otherwise: (schema) => schema.optional(),
    }),

  uploadInvoice: yup
    .mixed<File | string>()
    .nullable()
    .optional()
    .test(
      'file-or-string',
      'Invalid file type',
      (value) => value === null || typeof value === 'string' || value instanceof File,
    ),
});

export const settlementsEditFormSchema = yup.object({
  id: yup.number(),
  caseReferenceId: yup.string(),
  caseSystemName: yup.string(),
  firm: yup.string(),
  plaintiff: yup.object({
    id: yup.number(),
    fullName: yup.string(),
  }),
  defendant: yup.object({
    id: yup.number(),
    label: yup.string(),
  }),
  deduction: yup.array().of(
    yup.object().shape({
      id: yup.number(),
      vendorName: yup.string(),
      description: yup.string(),
      memo: yup.string(),
      amount: yup.number().max(9999999999.99, 'Max value 9999999999.99'),
      paymentMethod: yup.string(),
      disposition: yup.string(),
      createdAt: yup.string(),
      isAssigned: yup.boolean(),
      type: yup.string(),
      defendant: yup.string(),
      docFileName: yup.string(),
      isAdded: yup.boolean(),
    }),
  ),
  expenses: yup.array().of(
    yup.object().shape({
      id: yup.number(),
      vendorName: yup.string(),
      description: yup.string(),
      memo: yup.string(),
      amount: yup.number().max(9999999999.99, 'Max value 9999999999.99'),
      paymentMethod: yup.string(),
      disposition: yup.string(),
      createdAt: yup.string(),
      isAssigned: yup.boolean(),
      type: yup.string(),
      defendant: yup.string(),
      docFileName: yup.string(),
      isAdded: yup.boolean(),
    }),
  ),
  attorneysFees: yup.array().of(
    yup.object().shape({
      id: yup.number(),
      vendorName: yup.string(),
      description: yup.string(),
      memo: yup.string(),
      amount: yup.number().max(9999999999.99, 'Max value 9999999999.99'),
      paymentMethod: yup.string(),
      disposition: yup.string(),
      createdAt: yup.string(),
      isAssigned: yup.boolean(),
      type: yup.string(),
      defendant: yup.string(),
      docFileName: yup.string(),
    }),
  ),
  netSettlement: yup.array().of(
    yup.object().shape({
      id: yup.number(),
      vendorName: yup.string(),
      description: yup.string(),
      memo: yup.string(),
      amount: yup.number().max(9999999999.99, 'Max value 9999999999.99'),
      paymentMethod: yup.string(),
      disposition: yup.string(),
      createdAt: yup.string(),
      isAssigned: yup.boolean(),
      type: yup.string(),
      defendant: yup.string(),
      docFileName: yup.string(),
    }),
  ),
});

export const netSettlementEditFormSchema = yup.object({
  paymentIntention: yup.string().required('Required *'),
  externalPaymentMethod: yup
    .string()
    .nullable()
    .when('paymentIntention', {
      is: 'EXTERNAL',
      then: (schema) => schema.required('Required *'),
      otherwise: (schema) => schema.optional(),
    }),
  externalPaymentReference: yup
    .string()
    .nullable()
    .when('paymentIntention', {
      is: 'EXTERNAL',
      then: (schema) => schema.required('Required *'),
      otherwise: (schema) => schema.optional(),
    })
    .max(255, 'Must be less or equal to 255 characters'),
  externalPaymentMemo: yup
    .string()
    .nullable()
    .optional()
    .max(1000, 'Must be less or equal to 1000 characters'),
  externalPaymentDate: yup
    .date()
    .typeError('Invalid date')
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .when('paymentIntention', {
      is: 'EXTERNAL',
      then: (schema) => schema.required('Required *'),
      otherwise: (schema) => schema.optional(),
    })
    .max(new Date(), 'Date cannot be in the future'),
});

export const attorneyFeeEditPopupFormSchema = yup.object({
  disposition: yup.string().required('Required *'),
  paymentMethod: yup.string().nullable(),
  externalPaymentReference: yup
    .string()
    .nullable()
    .max(255, 'Must be less or equal to 255 characters'),
  externalPaymentMemo: yup
    .string()
    .nullable()
    .optional()
    .max(1000, 'Must be less or equal to 1000 characters'),
  externalPaymentDate: yup
    .date()
    .typeError('Invalid date')
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .max(new Date(), 'Date cannot be in the future'),
});

export const attorneyFeeEditFormSchema = yup.object({
  name: yup.string().max(100, 'Must be less or equal to 100 characters').required('Required *'),
  fee: yup.number().required('Required *'),
  role: yup.string().required('Required *'),
});

export const deductionDraftEditFormSchema = yup.object({
  type: yup.string().required('Required *'),
  vendorName: yup.object().when('type', {
    is: (value: string) => value === 'Lien' || value === 'SERVICE_PROVIDER',
    then: (schema) =>
      schema.shape({
        id: yup.string().optional().nullable(),
        label: yup.string().required('Required *'),
        defaultPaymentMethod: yup.string().optional().nullable(),
      }),
    otherwise: (schema) => schema.optional(),
  }),
  amount: yup
    .number()
    .typeError('Only numeric values are allowed')
    .min(0.01, 'Invalid amount')
    .max(99999999.99, 'Invalid amount')
    .required('Required *'),
  vendorParticipationId: yup.string().nullable().optional(),
  description: yup.string().max(1000, 'Must be less or equal to 1000 characters'),
  paymentMethod: yup.string().nullable().optional(),
  paymentAccount: yup
    .object({
      id: yup.string().optional().nullable(),
      label: yup.string().optional().nullable(),
    })
    .nullable()
    .optional(),
});

export const documentsCreateFormSchema = yup.object({
  name: yup.string().max(100, 'Must be less or equal to 100 characters').required('Required *'),
});

export const documentsEditFormSchema = yup.object({
  id: yup.string(),
  name: yup.string().max(100, 'Must be less or equal to 100 characters').required('Required *'),
  case: yup.object().shape({
    id: yup.string().required('Required *'),
    name: yup.string().required('Required *'),
    referenceId: yup.string().required('Required *'),
  }),
  plaintiffFullName: yup.string().required('Required *'),
  plaintiffId: yup.string(),
  fileId: yup.string(),
  documentUrl: yup.string(),
});

export const signaturesCreateFormSchema = yup.object({
  caseId: yup.object().shape({
    id: yup.string().required('Required *'),
    label: yup.string().required('Required *'),
  }),
  name: yup.string().max(100, 'Must be less or equal to 100 characters').required('Required *'),
  documentId: yup.string().required('Required *'),
});

export const signaturesEditFormSchema = yup.object({
  id: yup.string(),
  name: yup.string().max(100, 'Must be less or equal to 100 characters').required('Required *'),
  documentId: yup.string().required('Required *'),
  case: yup.object().shape({
    id: yup.string().required('Required *'),
    name: yup.string().required('Required *'),
  }),
  firm: yup.object().shape({
    id: yup.string().required('Required *'),
    name: yup.string().required('Required *'),
  }),
  plaintiffFullName: yup.string(),
  documentUrl: yup.string(),
  fileId: yup.string(),
});

export const dataCreateFormSchema = yup.object({
  caseId: yup.number().min(1).required(),
  name: yup.string().max(100, 'Must be less or equal to 100 characters').required('Required *'),
  notes: yup.string().max(1000, 'Must be less or equal to 1000 characters'),
  message: yup.string().max(1000, 'Must be less or equal to 1000 characters'),
});

const itemSchema = yup.object().shape({
  id: yup.string().required('Required *'),
  code: yup.string().max(100, 'Max length 100').required('Required *'),
  systemName: yup.string().max(100, 'Max length 100').required('Required *'),
  questionText: yup.string().max(1000, 'Max length 1000').required('Required *'),
  immutable: yup.boolean().required('Required *'),
  dependsOnItem: yup.object().shape({
    booleanItemId: yup.string(),
    expectedValue: yup
      .string()
      .when('booleanItemId', ([booleanItemId], schema) =>
        booleanItemId ? schema.oneOf(Object.values(ExpectedAnswer)).required('Required *') : schema,
      ),
  }),
  format: yup.string().oneOf(Object.values(QuestionnaireItemFormat)).required('Required *'),
  placeholder: yup.string().max(100, 'Max length 100'),
  allowPartial: yup.boolean(),
});

export const questionnaireFormSectionSchema = yup.object().shape({
  title: yup.string().max(100, 'Must be less or equal to 100 characters').required('Required *'),
  items: yup
    .array()
    .of(itemSchema)
    .min(1, 'You must add at least 1 item')
    .max(1000, 'The maximum number of items is 1000')
    .required('Required *'),
  immutable: yup.boolean().required('Required *'),
});

export const questionnaireFormSchema = yup.object().shape({
  sections: yup
    .array()
    .of(questionnaireFormSectionSchema)
    .min(1, 'You must add at least 1 section')
    .max(500, 'The maximum number of sections is 500')
    .required('Required *'),
});

export const questionnaireEditFormSchema = yup.object().shape({
  name: yup.string().max(100, 'Must be less or equal to 100 characters').required('Required *'),
  form: questionnaireFormSchema,
});

export const dataRequestQuestionnaireEditFormSchema = yup.object().shape({
  form: questionnaireFormSchema,
});

export const formSnippetSchema = yup.object().shape({
  items: yup
    .array()
    .of(itemSchema)
    .min(1, 'You must add at least 1 item')
    .max(1000, 'The maximum number of items is 1000')
    .required('Required *'),
});

export const formSnippetEditFormSchema = yup.object().shape({
  name: yup.string().max(100, 'Must be less or equal to 100 characters').required('Required *'),
  data: formSnippetSchema,
});

export const dataEditFormSchema = yup.object().shape({
  form: questionnaireFormSchema.nullable(),
  answers: yup
    .array()
    .of(
      yup.object().shape({
        itemId: yup.string().required(),
        format: yup.string().oneOf(Object.values(QuestionnaireItemFormat)).required(),
        value: yup.mixed().nullable(),
      }),
    )
    .required(),
  status: yup.string().required(),
  id: yup.number().required(),
  name: yup.string().max(100, 'Must be less or equal to 100 characters').required('Required *'),
  case: yup.object().shape({
    id: yup.number().required(),
    name: yup.string().required(),
    referenceId: yup.string().required(),
    type: yup.object().shape({
      id: yup.number().required(),
      name: yup.string().required(),
    }),
  }),
  firm: yup.object().shape({
    id: yup.number().required(),
    name: yup.string().required(),
  }),
  plaintiff: yup.object().shape({
    id: yup.number().required(),
    firstName: yup.string().required(),
    middleName: yup.string(),
    lastName: yup.string().required(),
  }),
  plaintiffFullName: yup.string().required(),
  notes: yup.string().max(1000, 'Must be less or equal to 1000 characters'),
  message: yup.string().max(1000, 'Must be less or equal to 1000 characters'),
  formItemsCount: yup.number().required(),
  formItemAnswersCount: yup.number().required(),
});

export const signInFormSchema = yup.object({
  email: yup.string().matches(EMAIL_REGEX, 'Invalid email format').required('Required *'),
  password: yup.string().required('Required *'),
});

export const changePasswordFormSchema = yup.object({
  newPassword: yup.string().required('Required *'),
  repeatNewPassword: yup
    .string()
    .required('Required *')
    // eslint-disable-next-line quotes
    .oneOf([yup.ref('newPassword'), ''], `Password doesn't match`),
});

export const forgotPasswordFormSchema = yup.object({
  email: yup.string().matches(EMAIL_REGEX, 'Invalid email format').required('Required *'),
});

export const resetPasswordFormSchema = yup.object({
  code: yup
    .string()
    .required('Required *')
    .matches(new RegExp('^.{6}$'), 'Invalid verification code'),
  newPassword: yup.string().required('Required *'),
  repeatNewPassword: yup
    .string()
    .required('Required *')
    // eslint-disable-next-line quotes
    .oneOf([yup.ref('newPassword'), ''], `Password doesn't match`),
});

const validateCurrencyValue = (min: number, max: number, message: string) => {
  return yup.string().test('isValidMaximumAmount', message, function (value) {
    if (!value) return true;

    const amount = Number(value.replace(/,/g, ''));
    if (Number.isNaN(amount)) return false;
    if (amount < min || amount > max) return false;
    return true;
  });
};

export const caseAttorneysFeesFormSchema = yup.object({
  caseFeesConfig: yup.object().shape({
    variableFeePercent: validateCurrencyValue(0, 100, 'Valid values are from 0 to 100'),
    maximumAmount: validateCurrencyValue(
      0,
      9999999999.99,
      'Valid values are from 0 to 9999999999.99',
    ),
    variableFeeBasis: yup.string().required(),
  }),
  secondaryFirms: yup
    .array()
    .of(
      yup
        .object()
        .shape({
          role: yup.string().oneOf(['CoCounsel', 'ReferringCounsel']).required(),
          id: yup.number().required(),
          name: yup.string().required(),
          config: yup
            .object()
            .shape({
              fixedFeeAmount: validateCurrencyValue(
                0,
                9999999999.99,
                'Valid values are from 0 to 9999999999.99',
              ),
              variableFeePercent: validateCurrencyValue(0, 100, 'Valid values are from 0 to 100'),
              variableFeeMaximumAmount: validateCurrencyValue(
                0,
                9999999999.99,
                'Valid values are from 0 to 9999999999.99',
              ),
              variableFeeBasis: yup.string().required(),
            })
            .required(),
        })
        .required(),
    )
    .min(0)
    .required(),
  newFirm: yup.object().shape({
    isAddingFirm: yup.boolean(),
    lawFirm: yup
      .object()
      .shape({
        id: yup.string().nullable(),
        label: yup.string().nullable(),
      })
      .nullable()
      .when('isAddingFirm', {
        is: true,
        then: (schema) =>
          schema.test(
            'firm-required',
            'Required *',
            (value) => Boolean(value?.id),
          ),
      }),

    role: yup
      .string()
      .nullable()
      .when('isAddingFirm', {
        is: true,
        then: (schema) => schema.required('Required *'),
      }),

    fixedFeeAmount: yup.string().nullable(),
    variableFeePercent: yup.string().nullable(),
    variableFeeMaximumAmount: yup.string().nullable(),
    variableFeeBasis: yup.string().nullable(),
  }),
});

const checkMemoSchema = yup
  .string()
  .when('paymentMethod', {
    is: (value: string | null) => (value || '').toUpperCase() === 'CHECK',
    then: (schema) => schema.max(140, 'Must be less or equal to 140 characters'),
    otherwise: (schema) => schema.optional(),
  })
  .defined();

export const settlementAttyFeesFormSchema = yup.object({
  leadFirm: yup
    .object()
    .shape({
      attyFeeId: yup.number().required(),
      id: yup.number().required(),
      name: yup.string().defined(),
      fee: validateCurrencyValue(0, 9999999999.99, 'Valid values are from 0 to 9999999999.99')
        .defined(),
    })
    .required(),
  secondaryCounsel: yup
    .array()
    .of(
      yup
        .object()
        .shape({
          attyFeeId: yup.number().required(),
          id: yup.number().required(),
          counselEntityType: yup.mixed<'FIRM' | 'VENDOR'>().oneOf(['FIRM', 'VENDOR']).required(),
          name: yup.string().defined(),
          role: yup.mixed<'CoCounsel' | 'ReferringCounsel'>()
            .oneOf(['CoCounsel', 'ReferringCounsel'])
            .required('Required *'),
          fee: validateCurrencyValue(0, 9999999999.99, 'Valid values are from 0 to 9999999999.99')
            .defined(),
          paymentMethod: yup.string().nullable().defined(),
          vendorPaymentAccountId: yup.mixed<number | string>().nullable().defined(),
          memo: checkMemoSchema,
        })
        .required(),
    )
    .min(0)
    .required(),
  newCounsels: yup
    .array()
    .of(
      yup
        .object()
        .shape({
          entity: yup
            .object()
            .shape({
              id: yup.string().nullable(),
              label: yup.string().nullable(),
            })
            .nullable()
            .defined(),
          role: yup.string().nullable().defined(),
          fee: validateCurrencyValue(0, 9999999999.99, 'Valid values are from 0 to 9999999999.99')
            .defined(),
          paymentMethod: yup.string().defined(),
          paymentAccount: yup
            .object()
            .shape({
              id: yup.string().nullable(),
              label: yup.string().nullable(),
            })
            .nullable()
            .defined(),
          memo: checkMemoSchema,
        })
        .required(),
    )
    .min(0)
    .required(),
});

export const autoSweepConfigFormSchema = yup.object({
  autoSweepEnabled: yup.boolean().required('Required *'),
  destinationAccount: yup.string().required('Required *'),
  sweepTiming: yup.string().oneOf(['immediate', 'scheduled']).required('Required *'),
  sweepAmount: yup.string().required('Required *'),
  sweepDayOfWeek: yup
    .string()
    .oneOf(['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'])
    .required('Required *'),
});

export const requestDemoSchema = yup.object({
  userName: yup
    .string()
    .max(100, 'Must be less or equal to 100 characters')
    .required('Required *')
    .matches(/^[a-zA-Z0-9-\s]+$/, 'Invalid name. Valid characters: a-z, A-Z, 0-9, -'),
  email: yup.string().matches(EMAIL_REGEX, 'Invalid email format').required('Required *'),
  message: yup
    .string()
    .max(1000, 'Must be less or equal to 1000 characters')
    .required('Required *'),
  isAgreed: yup
    .boolean()
    .required('Agreement must be accepted')
    .oneOf([true], 'Agreement must be accepted'),
});

export const accountDeletionRequestFormSchema = yup.object({
  email: yup.string().required('* Required').matches(EMAIL_REGEX, 'Must be a valid email'),
});

export const requestSupportFormSchema = yup.object({
  name: yup
    .string()
    .max(100, 'Must be less or equal to 100 characters')
    .required('Required *')
    .matches(/^[a-zA-Z0-9-\s]+$/, 'Invalid name. Valid characters: a-z, A-Z, 0-9, -'),
  email: yup.string().matches(EMAIL_REGEX, 'Invalid email format').required('Required *'),
  message: yup
    .string()
    .max(1000, 'Must be less or equal to 1000 characters')
    .required('Required *'),
});

export const emailConfirmationFormSchema = yup.object({
  ssn: yup
    .string()
    .required('Required *')
    .matches(
      /^(?!0{3})\d{3}-(?!0{2})\d{2}-(?!0{4})\d{4}$/,
      'Invalid SSN. Correct format: XXX-XX-XXXX',
    ),
  birthdate: yup
    .date()
    .transform((_, rawValue) => {
      const correctDate = moment(rawValue, ['mm-dd-yyyy']).toDate();
      return correctDate;
    })
    .min(moment(new Date(1900, 1, 1)).toDate(), 'Date is too early')
    .typeError('Invalid date')
    .required('Required *')
    .nullable()
    .max(moment(), 'Date is too far'),
});

export const editClientSSNDoBFormSchema = yup.object({
  ssn: yup
    .string()
    .required('Required *')
    .matches(
      /^(?!0{3})\d{3}-(?!0{2})\d{2}-(?!0{4})\d{4}$/,
      'Invalid SSN. Correct format: XXX-XX-XXXX',
    ),
  birthdate: yup
    .date()
    .transform((_, rawValue) => {
      const correctDate = moment(rawValue, ['yyyy-mm-dd']).toDate();
      return correctDate;
    })
    .min(moment(new Date(1900, 1, 1)).toDate())
    .typeError('Invalid date')
    .required('Required *')
    .nullable()
    .max(moment(), 'Date is too far'),
});

export const confirm2FACodeFormSchema = yup.object({
  verificationCode: yup.string().required('Required *'),
});

export const firmBankingCreateUnrestrictedAccountFormSchema = yup.object().shape({
  name: yup.string().max(40, 'Must be less or equal to 40 characters').required('Required *'),
  type: yup.string().required('Required *'),
  accountNumber: yup
    .string()
    .required('Required *')
    .matches(/^[a-zA-Z0-9*:#-/&,+'`]{1,17}$/, 'Invalid format'),
  routingNumber: yup
    .string()
    .required('Required *')
    .matches(/^\d{9}$/, 'Invalid format'),
});

// Vendor (participation) address fields are all optional; format rules apply only to filled values.
const participationAddressSchema = {
  participationAddress1: yup
    .string()
    .optional()
    .test(
      'address1-min-length',
      'Must be between 4 and 40 characters ',
      (value) => !value || value.length >= 4,
    )
    .max(40, 'Must be less or equal to 40 characters'),
  participationAddress2: yup
    .string()
    .max(30, 'Must be less or equal to 30 characters')
    .optional(),
  participationCity: yup
    .string()
    .optional()
    .max(45, 'Must be less or equal to 45 characters')
    // eslint-disable-next-line quotes
    .matches(/^[a-zA-Z.'-\s]+$/, { message: `Valid characters: a-z, A-Z, ., ', -`, excludeEmptyString: true }),
  participationState: yup.string().optional(),
  participationZip: yup.string().optional().matches(
    /^(?!0{5})(\d{5}|\d{5}-\d{4})$/,
    { message: 'Invalid Zip Code. Correct format: XXXXX or XXXXX-XXXX', excludeEmptyString: true },
  ),
};

export const CreateVendorAccountFormSchema = yup
  .object()
  .shape({
    name: yup.string().max(40, 'Must be less or equal to 40 characters').required('Required *'),
    tin: yup
      .string()
      .optional()
      .test(
        'tin-format',
        'Invalid TIN. Correct format: XX-XXXXXXX',
        (value) => !value || /^(\d{2})-(\d{7})$/.test(value),
      ),
    notes: yup.string().max(2000, 'Must be less or equal to 2000 characters').optional(),
    files: yup
      .mixed<File[]>()
      .optional()
      .test(
        'files-array',
        'Invalid files',
        (value) =>
          value === undefined ||
          value === null ||
          (Array.isArray(value) && value.every((f) => f instanceof File))
      ),
    // Vendor address fields (optional, all-or-nothing)
    ...participationAddressSchema,

    vendorType: yup.string().required('Required *'),

    // Required fields for all vendor accounts
    email: yup.string().required('Required *').matches(EMAIL_REGEX, 'Invalid email format'),

    isVendorConfirmationRequired: yup.boolean().required('Required *'),
    // Address verification is handled by the explicit confirmation flow in the form.
    // Keeping this optional avoids blocking submission before the confirmation popup can run.
    isAddressVerified: yup.boolean().optional(),
    isMailingAddressVerified: yup.boolean().when('checkMailingEnabled', {
      is: true,
      then: (schema) => schema.optional(),
      otherwise: (schema) => schema.optional(),
    }),

    // Payment method selection (at least one required)
    achEnabled: yup.boolean(),
    checkMailingEnabled: yup.boolean(),
    defaultAchEnabled: yup.boolean().optional(),
    defaultCheckMailingEnabled: yup.boolean().optional(),
    sameAsVendorAddress: yup.boolean().optional(),

    // ACH fields (required when ACH is enabled)
    accountType: yup.string().when('achEnabled', {
      is: true,
      then: (schema) => schema.required('Required *'),
      otherwise: (schema) => schema.optional(),
    }),
    accountNumber: yup.string().when('achEnabled', {
      is: true,
      then: (schema) =>
        schema
          .required('Required *')
          .matches(
            /^[0-9]{4,17}$/,
            'Invalid account number. Account number must be 4-17 digits and cannot be all zeros.',
          )
          .test(
            'not-all-zero-account-number',
            'Invalid account number. Account number must be 4-17 digits and cannot be all zeros.',
            (value) => !value || !/^0+$/.test(value),
          ),
      otherwise: (schema) => schema.optional(),
    }),
    routingNumber: yup.string().when('achEnabled', {
      is: true,
      then: (schema) =>
        schema
          .required('Required *')
          .matches(
            /^\d{9}$/,
            'Invalid routing number. Please enter a valid 9-digit routing number.',
          )
          .test(
            'routing-checksum',
            'Invalid routing number. Please enter a valid 9-digit routing number.',
            (value) => {
              if (!value || !/^\d{9}$/.test(value)) return false;
              const d = value.split('').map(Number);
              const checksum =
                (3 * (d[0] + d[3] + d[6]) +
                  7 * (d[1] + d[4] + d[7]) +
                  (d[2] + d[5] + d[8])) %
                10;
              return checksum === 0;
            },
          ),
      otherwise: (schema) => schema.optional(),
    }),

    // Check mailing fields (required when check mailing is enabled)
    mailingAddress1: yup.string().when('checkMailingEnabled', {
      is: true,
      then: (schema) =>
        schema
          .required('Required *')
          .min(4, 'Must be between 4 and 40 characters ')
          .max(40, 'Must be less or equal to 40 characters'),
      otherwise: (schema) => schema.optional(),
    }),
    mailingAddress2: yup.string().max(30, 'Must be less or equal to 30 characters').optional(),
    mailingCity: yup.string().when('checkMailingEnabled', {
      is: true,
      then: (schema) =>
        schema
          .required('Required *')
          .max(45, 'Must be less or equal to 45 characters')
          // eslint-disable-next-line quotes
          .matches(/^[a-zA-Z.'-\s]+$/, `Valid characters: a-z, A-Z, ., ', -`),
      otherwise: (schema) => schema.optional(),
    }),
    mailingState: yup.string().when('checkMailingEnabled', {
      is: true,
      then: (schema) => schema.required('Required *'),
      otherwise: (schema) => schema.optional(),
    }),
    mailingZip: yup.string().when('checkMailingEnabled', {
      is: true,
      then: (schema) =>
        schema
          .required('Required *')
          .matches(
            /^(?!0{5})(\d{5}|\d{5}-\d{4})$/,
            'Invalid Zip Code. Correct format: XXXXX or XXXXX-XXXX',
          ),
      otherwise: (schema) => schema.optional(),
    }),
    mailingPhoneNumber: yup.string().when('checkMailingEnabled', {
      is: true,
      then: (schema) =>
        schema
          .required('Required *')
          .test('phone-format', 'Invalid US mobile number', function (value) {
            if (!value || value === '') return false; // Don't allow empty values when required
            // Accept +1XXXXXXXXXX or +1-XXX-XXX-XXXX using NANP-valid area/exchange prefixes.
            return (
              /^\+1[2-9]\d{2}[2-9]\d{2}\d{4}$/.test(value) ||
              /^\+1-[2-9]\d{2}-[2-9]\d{2}-\d{4}$/.test(value)
            );
          }),
      otherwise: (schema) => schema.optional(),
    }),
    contactName: yup
      .string()
      .max(50, 'Must be less or equal to 50 characters')
      .required('Required *'),
    externalVendorId: yup
      .string()
      .nullable()
      .transform((value, originalValue) => {
        // If empty string or null/undefined, return null
        if (!originalValue || originalValue === '') return null;
        return value;
      })
      .max(100, 'Must be less or equal to 100 characters'),
  })
  .test(
    'payment-method-required',
    'At least one payment method must be selected',
    function (values) {
      const { achEnabled, checkMailingEnabled } = values || {};

      // Ensure at least one payment method is selected (should be OR, not AND)
      if (achEnabled !== true && checkMailingEnabled !== true) {
        return this.createError({
          message: 'At least one payment method (ACH or Check mailing) must be selected',
          path: 'achEnabled',
        });
      }
      return true;
    },
  );

export const EditVendorAccountFormSchema = yup
  .object()
  .shape({
    name: yup.string().max(40, 'Must be less or equal to 40 characters').required('Required *'),
    tin: yup
      .string()
      .optional()
      .test(
        'tin-format',
        'Invalid TIN. Correct format: XX-XXXXXXX',
        (value) => !value || /^(\d{2})-(\d{7})$/.test(value),
      ),

    notes: yup.string().max(2000, 'Must be less or equal to 2000 characters').optional(),

    // Vendor address fields (optional, all-or-nothing)
    ...participationAddressSchema,

    vendorType: yup.string().required('Required *'),

    // Required fields for all vendor accounts
    email: yup.string().required('Required *').matches(EMAIL_REGEX, 'Invalid email format'),

    isVendorConfirmationRequired: yup.boolean().required('Required *'),
    isAddressVerified: yup.boolean().optional(),
    isMailingAddressVerified: yup.boolean().optional(),
    // .when('checkMailingEnabled', {
    //   is: true,
    //   then: (schema) => schema.oneOf([true], 'Check mailing address must be verified').required('Check mailing address verification is required'),
    //   otherwise: (schema) => schema.optional(),
    // }),

    // Payment method selection (at least one required)
    achEnabled: yup.boolean(),
    checkMailingEnabled: yup.boolean(),
    defaultAchEnabled: yup.boolean().optional(),
    defaultCheckMailingEnabled: yup.boolean().optional(),
    sameAsVendorAddress: yup.boolean().optional(),

    // ACH fields (required when ACH is enabled)
    accountType: yup.string().when('achEnabled', {
      is: true,
      then: (schema) => schema.required('Required *'),
      otherwise: (schema) => schema.optional(),
    }),
    accountNumber: yup.string().when('achEnabled', {
      is: true,
      then: (schema) =>
        schema
          .required('Required *')
          .test('account-number-format', 'Invalid format', function (value) {
            if (!value || value === '************') return true; // Allow masked/hidden placeholder
            return /^[a-zA-Z0-9*:#-/&,+'`]{1,20}$/.test(value);
          }),
      otherwise: (schema) => schema.optional(),
    }),
    routingNumber: yup.string().when('achEnabled', {
      is: true,
      then: (schema) =>
        schema.required('Required *').test('routing-format', 'Invalid format', function (value) {
          if (!value) return false;
          return /^\d{9}$/.test(value);
        }),
      otherwise: (schema) => schema.optional(),
    }),

    // Check mailing fields (required when check mailing is enabled)
    mailingAddress1: yup.string().when('checkMailingEnabled', {
      is: true,
      then: (schema) =>
        schema
          .required('Required *')
          .min(4, 'Must be between 4 and 40 characters ')
          .max(60, 'Must be less or equal to 60 characters'),
      otherwise: (schema) => schema.optional(),
    }),
    mailingAddress2: yup.string().max(30, 'Must be less or equal to 30 characters').optional(),
    mailingCity: yup.string().when('checkMailingEnabled', {
      is: true,
      then: (schema) =>
        schema
          .required('Required *')
          .max(45, 'Must be less or equal to 45 characters')
          // eslint-disable-next-line quotes
          .matches(/^[a-zA-Z.'-\s]+$/, `Valid characters: a-z, A-Z, ., ', -`),
      otherwise: (schema) => schema.optional(),
    }),
    mailingState: yup.string().when('checkMailingEnabled', {
      is: true,
      then: (schema) => schema.required('Required *'),
      otherwise: (schema) => schema.optional(),
    }),
    mailingZip: yup.string().when('checkMailingEnabled', {
      is: true,
      then: (schema) =>
        schema
          .required('Required *')
          .matches(
            /^(?!0{5})(\d{5}|\d{5}-\d{4})$/,
            'Invalid Zip Code. Correct format: XXXXX or XXXXX-XXXX',
          ),
      otherwise: (schema) => schema.optional(),
    }),
    mailingPhoneNumber: yup.string().when('checkMailingEnabled', {
      is: true,
      then: (schema) =>
        schema
          .required('Required *')
          .test('phone-format', 'Invalid US mobile number', function (value) {
            if (!value || value === '') return false; // Don't allow empty values when required
            // Accept +1XXXXXXXXXX or +1-XXX-XXX-XXXX using NANP-valid area/exchange prefixes.
            return (
              /^\+1[2-9]\d{2}[2-9]\d{2}\d{4}$/.test(value) ||
              /^\+1-[2-9]\d{2}-[2-9]\d{2}-\d{4}$/.test(value)
            );
          }),
      otherwise: (schema) => schema.optional(),
    }),
    contactName: yup
      .string()
      .max(50, 'Must be less or equal to 50 characters')
      .required('Required *'),
  })
  .test(
    'payment-method-required',
    'At least one payment method must be selected',
    function (values) {
      const { achEnabled, checkMailingEnabled } = values || {};

      // Ensure at least one payment method is selected (should be OR, not AND)
      if (achEnabled !== true && checkMailingEnabled !== true) {
        return this.createError({
          message: 'At least one payment method (ACH or Check mailing) must be selected',
          path: 'achEnabled',
        });
      }
      return true;
    },
  );

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const createEditBankingDetailsFormSchema = (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  hasAnyAchFieldEntered: boolean,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  hasAnyCheckFieldEntered: boolean,
) =>
  yup.object().shape({
    name: yup.string().required('Required *'),
    defaultPaymentMethod: yup.string().required('Required *'),

    // Account details - required if defaultPaymentMethod is 'ACH'
    accountType: yup.string().when('defaultPaymentMethod', {
      is: (defaultPaymentMethod: string) => defaultPaymentMethod === 'ACH',
      then: (schema) => schema.required('Required *'),
      otherwise: (schema) => schema.optional(),
    }),
    accountNumber: yup
      .string()
      .when('defaultPaymentMethod', {
        is: (defaultPaymentMethod: string) => defaultPaymentMethod === 'ACH',
        then: (schema) => schema.required('Required *'),
        otherwise: (schema) => schema.optional(),
      })
      .test('account-number-format', 'Invalid format', function (value) {
        if (!value || value === '************') return true; // Allow masked/hidden placeholder
        return /^[a-zA-Z0-9*:#-/&,+'`]{1,17}$/.test(value);
      }),
    routingNumber: yup
      .string()
      .when('defaultPaymentMethod', {
        is: (defaultPaymentMethod: string) => defaultPaymentMethod === 'ACH',
        then: (schema) => schema.required('Required *'),
        otherwise: (schema) => schema.optional(),
      })
      .test('routing-format', 'Invalid format', function (value) {
        if (!value) return true;
        return /^\d{9}$/.test(value);
      }),

    // Address details - required if defaultPaymentMethod is 'CHECK' OR any CHECK field is entered
    mailingAddress1: yup
      .string()
      .when('defaultPaymentMethod', {
        is: (defaultPaymentMethod: string) => defaultPaymentMethod === 'CHECK',
        then: (schema) => schema.required('Required *'),
        otherwise: (schema) => schema.optional(),
      })
      .test('address-format', 'Must be between 4 and 40 characters', function (value) {
        if (!value) return true;
        return value.length >= 4 && value.length <= 40;
      }),
    mailingAddress2: yup.string().max(30, 'Must be less or equal to 30 characters').optional(),
    mailingCity: yup
      .string()
      .when('defaultPaymentMethod', {
        is: (defaultPaymentMethod: string) => defaultPaymentMethod === 'CHECK',
        then: (schema) => schema.required('Required *'),
        otherwise: (schema) => schema.optional(),
      })
      // eslint-disable-next-line quotes
      .test('city-format', "Valid characters: a-z, A-Z, ., '-,", function (value) {
        if (!value) return true;
        return /^[a-zA-Z.'-\s]+$/.test(value) && value.length <= 45;
      }),
    mailingState: yup.string().when('defaultPaymentMethod', {
      is: (defaultPaymentMethod: string) => defaultPaymentMethod === 'CHECK',
      then: (schema) => schema.required('Required *'),
      otherwise: (schema) => schema.optional(),
    }),
    mailingZipCode: yup
      .string()
      .when('defaultPaymentMethod', {
        is: (defaultPaymentMethod: string) => defaultPaymentMethod === 'CHECK',
        then: (schema) => schema.required('Required *'),
        otherwise: (schema) => schema.optional(),
      })
      .test(
        'zip-format',
        'Invalid Zip Code. Correct format: XXXXX or XXXXX-XXXX',
        function (value) {
          if (!value) return true;
          return /^(?!0{5})(\d{5}|\d{5}-\d{4})$/.test(value);
        },
      ),
    phoneNumber: yup
      .string()
      .when('defaultPaymentMethod', {
        is: (defaultPaymentMethod: string) => defaultPaymentMethod === 'CHECK',
        then: (schema) => schema.required('Required *'),
        otherwise: (schema) => schema.optional(),
      })
      .test('phone-format', 'Invalid phone number', function (value) {
        if (!value || value === '') return true; // Allow empty values when optional
        // Accept both formats: +18987676767 or +1-898-767-6767
        return /^\+1\d{10}$/.test(value) || /^\+1-\d{3}-\d{3}-\d{4}$/.test(value);
      }),

    // Participation address fields (optional, all-or-nothing)
    ...participationAddressSchema,
    email: yup.string().required('Required *').matches(EMAIL_REGEX, 'Invalid email format'),
    contactName: yup
      .string()
      .max(50, 'Must be less or equal to 50 characters')
      .required('Required *'),
  });

export const firmTrustAccountFormSchema = yup.object().shape({
  name: yup.string().max(50, 'Must be less or equal to 50 characters').required('Required *'),
  type: yup.string().required('Required *'),
  accountNumber: yup
    .string()
    .required('Required *')
    .matches(/^[a-zA-Z0-9*:#-/&,+'`]{1,17}$/, 'Invalid format'),
  routingNumber: yup
    .string()
    .required('Required *')
    .matches(/^\d{9}$/, 'Invalid format'),
});

export const firmWithdrawFundsFormSchema = yup.object().shape({
  fromSystemAccountType: yup.string().required('Required *'),
  toUnrestrictedAccountId: yup.string().required('Required *'),
  amount: yup.string().required('Required *'),
});

export const dataFileDownloadTemplateFormSchema = yup.object().shape({
  fileType: yup.string().required('Required *'),
});

export const dataFileUploadFormSchema = yup.object().shape({
  fileType: yup.string().required('Required *'),
  fileName: yup.string().required('Required *'),
  sendVia: yup.string(),
  access: yup.string(),
  sendInvitationVia: yup.boolean(),
  fileData: yup.mixed(),
});

export const pinInputFormSchema = yup
  .array()
  .of(
    yup
      .object()
      .shape({
        value: yup.string().required(),
      })
      .required(),
  )
  .required();

export const verificationPinFormSchema = yup
  .object()
  .shape({
    numbers: pinInputFormSchema.required(),
  })
  .required();

export const paySettlementFormSchema = yup.object().shape({
  numbers: pinInputFormSchema.required(),
});

export const payoutAchSchema = yup.object({
  accountHolderName: yup.string().required('Required *'),
  accountType: yup.string().required('Required *'),
  routingNumber: yup
    .string()
    .required('Required *')
    .matches(/^\d{9}$/, 'Must be 9 digits'),
  accountNumber: yup.string(),
  confirmAccountNumber: yup.string(),
});

export const payoutCheckSchema = yup.object({
  line1: yup.string().required('Required *'),
  line2: yup.string(),
  city: yup
    .string()
    .max(45, 'Must be less or equal to 45 characters')
    .required('Required *')
    // eslint-disable-next-line quotes
    .matches(/^[a-zA-Z.'-\s]+$/, `Valid characters: a-z, A-Z, ., ', -`),
  state: yup.string().required('Required *'),
  zip: yup
    .string()
    .required('Required *')
    .matches(
      /^(?!0{5})(\d{5}|\d{5}-\d{4})$/,
      'Invalid Zip Code. Correct format: XXXXX or XXXXX-XXXX',
    ),
  phone: yup
    .string()
    .required('Required *')
    .matches(/^\+1\d{10}$/, 'Invalid phone number'),
});

export const lienRecoveryFormSchema = (maxRecoverableAmount: number, minDateReceived?: Moment) =>
  yup.object().shape({
    amount: yup
      .string()
      .required('Required *')
      .test(
        'isPositive',
        'Amount must be greater than $0.00',
        (value) => Number((value || '').replace(/,/g, '')) > 0,
      )
      .test(
        'isWithinMax',
        'Amount cannot exceed the max recoverable amount',
        (value) => Number((value || '').replace(/,/g, '')) <= maxRecoverableAmount,
      ),
    dateReceived: validateDateField(undefined, moment().endOf('day').toISOString()).test(
      'isNotBeforePaidDate',
      `Date cannot be before the paid date${minDateReceived ? ` (${minDateReceived.format('M/D/YYYY')})` : ''}`,
      (value) => !minDateReceived || !value || !moment(value as Moment).isBefore(minDateReceived, 'day'),
    ),
    reason: yup.string().required('Required *'),
    note: yup.string().max(1000, 'Must be less or equal to 1000 characters'),
    attachment: yup.mixed().nullable(),
  });
  
type DynamicVendorFormValues = Record<
  string,
  string | number | boolean | undefined
>;

const ACCOUNTING_VENDOR_ADDRESS_FIELD_NAMES = ['address1', 'address2', 'city', 'state', 'zip', 'country'];

const createAccountingVendorFieldSchema = (
  field: AccountingVendorField,
): yup.AnySchema => {
  switch (field.type) {
    case 'string': {
      let schema = yup.string();

      schema = schema.transform((value) => {
        if (typeof value !== 'string') {
          return value;
        }

        const transformedValue = field.trim
          ? value.trim()
          : value;

        // Optional empty values should not fail min/max validation.
        if (
          !field.required &&
          transformedValue === ''
        ) {
          return undefined;
        }

        return transformedValue;
      });

      if (field.noWhitespaceOnly) {
        schema = schema.test(
          'no-whitespace-only',
          `${field.label} cannot contain only whitespace`,
          (value) => {
            if (
              value === undefined ||
              value === null ||
              value === ''
            ) {
              return true;
            }

            return value.trim().length > 0;
          },
        );
      }

      if (field.minLength !== undefined) {
        schema = schema.min(
          field.minLength,
          field.maxLength !== undefined
            ? `Must be between ${field.minLength} and ${field.maxLength} characters`
            : `Must be at least ${field.minLength} characters`,
        );
      }

      if (field.maxLength !== undefined) {
        schema = schema.max(
          field.maxLength,
          `Must be less or equal to ${field.maxLength} characters`,
        );
      }

      if (field.format === 'email') {
        schema = schema.matches(EMAIL_REGEX, {
          message: 'Invalid email format',
          excludeEmptyString: true,
        });
      }

      if (field.required) {
        schema = schema.required('Required *');
      } else {
        schema = schema.optional();
      }

      return schema;
    }

    case 'number': {
      let schema = yup
        .number()
        .typeError('Invalid number');

      if (field.required) {
        schema = schema.required('Required *');
      } else {
        schema = schema.optional();
      }

      return schema;
    }

    case 'boolean': {
      let schema = yup.boolean();

      if (field.required) {
        schema = schema.required('Required *');
      } else {
        schema = schema.optional();
      }

      return schema;
    }

    default:
      return yup.mixed().optional();
  }
};

export const QuickBooksVendorFormSchema = (
  config?: AccountingVendor | null,
): yup.ObjectSchema<DynamicVendorFormValues> => {
  const schemaShape: Record<
    string,
    yup.AnySchema
  > = {};

  const hasAddressField = Boolean(
    config?.fields?.some((field) => field.name === 'address1') ||
    config?.providerFields?.some((field) => field.name === 'address1'),
  );
  const hasAnyAddressValue = (...values: unknown[]) =>
    values.some((value) => typeof value === 'string' && value.trim() !== '');

  if (config) {
    const fields = [
      ...(config.fields ?? []),
      ...(config.providerFields ?? []),
    ];

    fields.forEach((field) => {
      schemaShape[field.name] =
        createAccountingVendorFieldSchema(field);
    });
  }

  schemaShape.isAddressConfirmationCompleted = hasAddressField
    ? yup.boolean().when(ACCOUNTING_VENDOR_ADDRESS_FIELD_NAMES, {
      is: (...values: unknown[]) => hasAnyAddressValue(...values),
      then: (schema) =>
        schema
          .oneOf([true], 'Vendor address confirmation must be completed')
          .required('Vendor address confirmation is required'),
      otherwise: (schema) => schema.optional(),
    })
    : yup.boolean().optional();

  schemaShape.isAddressVerified = yup.boolean().optional();

  return yup.object(
    schemaShape,
  ) as yup.ObjectSchema<DynamicVendorFormValues>;
};